import React, { useState, useEffect } from 'react';
import {
  X,
  Trash2,
  Circle,
  Package,
  Users,
  Building2,
  Clock,
  Calendar,
  CalendarCheck,
  CalendarX,
  CalendarClock,
  Flag,
  Plus,
  Landmark,
  CircleX
} from 'lucide-react';
import { Button } from 'react-bootstrap';
import Select from 'react-select';
import CustomMultiSelect from '../CustomMultiSelect';
import AdvanceDateRangePicker from '../common/AdvanceDateRangePicker';
import moment from 'moment';
import { isClient, individualOrdersOption, calculateDateTimeRange, hasFilterValues } from '../../helpers/global';
import { FILTER_OPERATORS, orderStatusForClientMap } from '../../helpers/constants';

const defaultDateFilter = {
  startDate: null,
  endDate: null,
  key: 'selection',
  rangeType: 'All Time'
};

// Map filter keys to Lucide icons
const getFilterIcon = filterKey => {
  const iconMap = {
    status: Circle,
    orderType: Package,
    assignedProcessors: Users,
    organization: Building2,
    rush: Clock,
    dateDelivered: CalendarCheck,
    closedFile: CalendarCheck,
    createdAt: Calendar,
    dateCancelled: CalendarX,
    closingDate: CalendarClock,
    eta: CalendarClock,
    isEtaPastClosingDate: Flag,
    cancellationRequested: CircleX,
    county: Landmark,
    municipality: Building2
  };
  return iconMap[filterKey] || Calendar;
};

const formatOptionLabel = ({ label, icon: Icon, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    {Icon && (
      <Icon
        size={16}
        style={{ flexShrink: 0 }}
        className={value === 'isEtaPastClosingDate' ? 'filter-flag-icon-primary' : ''}
      />
    )}
    <span>{label}</span>
  </div>
);

// Shared Select component styles
const selectStyles = {
  control: base => ({
    ...base,
    height: '30px'
  }),
  option: base => ({
    ...base,
    fontSize: '14px'
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999
  }),
  menu: base => ({
    ...base,
    zIndex: 9999
  }),
  input: base => ({
    ...base,
    backgroundColor: 'transparent',
    margin: 0,
    padding: 0,
    height: '42px'
  }),
  valueContainer: base => ({
    ...base,
    backgroundColor: 'transparent',
    height: '30px'
  }),
  singleValue: base => ({
    ...base,
    height: '36px'
  })
};

const OrderFilterBuilder = ({
  onClose,
  title = 'Filters',
  style = {},
  setOrderFilterMetadata,
  allAvailableFilters,
  onApplyFilters
}) => {
  // Internal state for managing filters in the component (synced with allAvailableFilters)
  const [selectedFilters, setSelectedFilters] = useState(() => {
    if (!allAvailableFilters || !Array.isArray(allAvailableFilters)) return [];
    return allAvailableFilters.filter(filter => hasFilterValues(filter, filter.values));
  });

  const isClientRole = isClient(localStorage.getItem('user-role'));

  // Automatically add first filter if no filters are selected
  useEffect(() => {
    if (selectedFilters.length === 0 && allAvailableFilters.length > 0) {
      const firstFilter = allAvailableFilters[0];
      setSelectedFilters([
        {
          ...firstFilter,
          values: firstFilter.type === 'date' ? { startDate: null, endDate: null } : [],
          operator: firstFilter.operator || firstFilter.operatorOptions?.[0] || null
        }
      ]);
    }
  }, []);

  // Helper to get filter by key from selectedFilters
  const getSelectedFilter = filterKey => selectedFilters.find(f => f.key === filterKey);

  // Helper to get filter by key from allAvailableFilters (for getting base filter structure)
  const getFilter = filterKey => allAvailableFilters.find(f => f.key === filterKey);

  // Helper to convert stored original status values back to client-side labels for display
  const getStatusLabelsForDisplay = originalStatusValues => {
    if (!Array.isArray(originalStatusValues) || originalStatusValues.length === 0) return [];
    const clientLabels = originalStatusValues.map(status => orderStatusForClientMap[status]).filter(Boolean); // Remove undefined values
    return [...new Set(clientLabels)]; // Return unique labels
  };

  const handleAddFilter = () => {
    const selectedKeys = selectedFilters.map(f => f.key);
    const availableToAdd = allAvailableFilters.filter(f => !selectedKeys.includes(f.key));
    if (availableToAdd.length > 0) {
      const newFilter = availableToAdd[0];
      setSelectedFilters(prev => [
        ...prev,
        {
          ...newFilter,
          values: newFilter.type === 'date' ? { startDate: null, endDate: null } : [],
          operator: newFilter.operator || newFilter.operatorOptions?.[0] || null
        }
      ]);
    }
  };

  const handleFilterTypeChange = (currentFilterKey, newFilterKey) => {
    const newFilter = getFilter(newFilterKey);
    if (!newFilter) return;

    // Update selectedFilters - replace the filter object
    setSelectedFilters(prev =>
      prev.map(filter =>
        filter.key === currentFilterKey
          ? {
              ...newFilter,
              values: newFilter.type === 'date' ? { startDate: null, endDate: null } : [],
              operator: newFilter.operator || newFilter.operatorOptions?.[0] || null
            }
          : filter
      )
    );
  };

  const getAvailableFiltersForDropdown = currentFilterKey => {
    const selectedKeys = selectedFilters.map(f => f.key);
    return allAvailableFilters
      .filter(f => f.key === currentFilterKey || !selectedKeys.includes(f.key))
      .map(f => ({ value: f.key, label: f.label, icon: getFilterIcon(f.key) }));
  };
  const handleRemoveFilter = filterKey => {
    setSelectedFilters(prev => prev.filter(filter => filter.key !== filterKey));
  };

  const handleOperatorChange = (filterKey, operator) => {
    const newOperator = operator.value;

    // If operator changes on a date filter, clear both startDate and endDate
    setSelectedFilters(prev =>
      prev.map(filter => {
        if (filter.key === filterKey) {
          const updatedFilter = { ...filter, operator: newOperator };
          if (filter.type === 'date' && filter.values) {
            updatedFilter.values = {
              ...filter.values,
              startDate: null,
              endDate: null
            };
          }
          return updatedFilter;
        }
        return filter;
      })
    );
  };

  const handleFilterValuesChange = (key, values) => {
    const storedValues =
      key === 'status' && isClientRole ? values.map(value => value.value).flat() : values.map(value => value.value);
    setSelectedFilters(prev => prev.map(filter => (filter.key === key ? { ...filter, values: storedValues } : filter)));
  };

  // Helper function to format dates for filter metadata
  const formatStartDate = date =>
    date
      ? moment(date)
          .startOf('day')
          .toISOString()
      : null;
  const formatEndDate = date =>
    date
      ? moment(date)
          .endOf('day')
          .toISOString()
      : null;

  const handleDateRangeChange = (key, ranges) => {
    const startDate = formatStartDate(ranges.selection.startDate);
    const endDate = ranges.selection.endDate ? formatEndDate(ranges.selection.endDate) : null;

    // Update values in selectedFilters
    setSelectedFilters(prev =>
      prev.map(filter =>
        filter.key === key
          ? {
              ...filter,
              values: {
                startDate,
                endDate,
                rangeType: ranges.selection.rangeType || filter.values?.rangeType || 'All Time'
              }
            }
          : filter
      )
    );
  };

  const getDateFilterValue = key => {
    const filter = getSelectedFilter(key);
    const values = filter?.values;
    if (values && (values.startDate || values.endDate)) {
      return {
        startDate: values.startDate,
        endDate: values.endDate,
        key: 'selection',
        rangeType: values.rangeType || 'All Time'
      };
    }
    return defaultDateFilter;
  };

  const getHandleNavigateDate = key => {
    return (rangeType, direction) => {
      const currentRange = getDateFilterValue(key);

      let newRangeType;
      switch (rangeType) {
        case 'Last 12 Months':
        case 'This Year':
        case 'Last Year':
          newRangeType = 'Year';
          break;
        case 'Last 3 Months':
        case 'This Quarter':
        case 'Last Quarter':
          newRangeType = 'Quarter';
          break;
        case 'This Month':
        case 'Last Month':
          newRangeType = 'Month';
          break;
        case 'This Week':
        case 'Last Week':
          newRangeType = 'Week';
          break;
        case 'Today':
          newRangeType = 'Day';
          break;
        default:
          newRangeType = 'Month';
          break;
      }

      const { startDateTime, endDateTime } = calculateDateTimeRange(
        currentRange.startDate,
        currentRange.endDate,
        newRangeType,
        direction
      );

      const startDate = formatStartDate(startDateTime);
      const endDate = formatEndDate(endDateTime);

      // Update values in selectedFilters
      setSelectedFilters(prev =>
        prev.map(filter =>
          filter.key === key
            ? {
                ...filter,
                values: {
                  startDate,
                  endDate,
                  rangeType
                }
              }
            : filter
        )
      );
    };
  };

  const handleClearAll = () => {
    setSelectedFilters([]);
  };

  const handleApply = () => {
    const updatedMetadata = allAvailableFilters.map(filter => {
      const selectedFilter = selectedFilters.find(sf => sf.key === filter.key);
      if (selectedFilter) {
        // Use the selected filter's values and operator
        return selectedFilter;
      }
      // For filters not in selectedFilters, clear their values
      return {
        ...filter,
        values: filter.type === 'date' ? { startDate: null, endDate: null } : []
      };
    });

    onApplyFilters(updatedMetadata);
    onClose();
  };

  const selectedKeys = selectedFilters.map(f => f.key);
  const availableToAdd = allAvailableFilters.filter(f => !selectedKeys.includes(f.key));

  return (
    <div
      className="order-filter-modal"
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        minWidth: '700px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        zIndex: 1000,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7ea',
        marginTop: '8px',
        ...style
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center mb-2 border-bottom "
        style={{ padding: '12px 10px' }}
      >
        <h5 className="text-dark mb-0" style={{ fontSize: '15px', fontWeight: 600 }}>
          {title}
        </h5>
        <button
          onClick={onClose}
          style={{
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            padding: 0,
            width: '24px',
            height: '24px'
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* selected filters */}
      <div className="p-1" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {selectedFilters.length === 0 ? (
          <div className="text-muted text-center py-4">No filters added. Click "+ Add Filter" to add one.</div>
        ) : (
          selectedFilters.map(filter => {
            const dateFilterValue = filter.type === 'date' ? getDateFilterValue(filter.key) : null;
            const operator = filter.operator || null;
            const isDatePickerNeeded = operator !== FILTER_OPERATORS.TODAY_AND_BEFORE;
            const isFilterValueNeeded = isDatePickerNeeded;

            return (
              <div key={filter.key} className="mb-1 p-2 filter-builder-item">
                <div className="d-flex align-items-center" style={{ flexWrap: 'nowrap', gap: '8px' }}>
                  <div style={{ width: '35%', flexShrink: 0 }}>
                    <Select
                      classNamePrefix="filter-select-input"
                      value={{
                        value: filter.key,
                        label: filter.label,
                        icon: getFilterIcon(filter.key)
                      }}
                      onChange={option => handleFilterTypeChange(filter.key, option.value)}
                      options={getAvailableFiltersForDropdown(filter.key)}
                      formatOptionLabel={formatOptionLabel}
                      isSearchable={false}
                      placeholder="Search filter..."
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectStyles}
                    />
                  </div>

                  {filter.operatorOptions && filter.operatorOptions.length > 0 && (
                    <div style={{ minWidth: '18%', flexShrink: 0 }}>
                      <Select
                        classNamePrefix="filter-select-input"
                        value={operator ? { value: operator, label: operator } : null}
                        onChange={option => handleOperatorChange(filter.key, option)}
                        options={filter.operatorOptions.map(op => ({ value: op, label: op }))}
                        placeholder={FILTER_OPERATORS.IS}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                      />
                    </div>
                  )}

                  {isFilterValueNeeded && (
                    <div style={{ width: '37%', flexShrink: 0 }}>
                      {filter.type === 'date' ? (
                        <div style={{ position: 'relative', zIndex: 1000 }}>
                          <AdvanceDateRangePicker
                            value={dateFilterValue}
                            onChange={ranges => handleDateRangeChange(filter.key, ranges)}
                            className="w-100"
                            extraStyleForSlideBarFilter={true}
                            showNavigator={false}
                            rangeType={dateFilterValue?.rangeType || 'All Time'}
                            handleNavigateDate={getHandleNavigateDate(filter.key)}
                            isFloating={true}
                            topOffset={isClientRole ? 200 : 0}
                            allowAllTime={false}
                            singleDateMode={operator === FILTER_OPERATORS.AFTER || operator === FILTER_OPERATORS.BEFORE}
                          />
                        </div>
                      ) : (
                        <CustomMultiSelect
                          className="filter-multi-select-input"
                          id={filter.key}
                          onChange={values => handleFilterValuesChange(filter.key, values)}
                          selectedOptions={
                            Array.isArray(filter.values)
                              ? filter.key === 'status' && isClientRole
                                ? getStatusLabelsForDisplay(filter.values)
                                : filter.values
                              : []
                          }
                          options={
                            filter.key === 'organization' ? [individualOrdersOption, ...filter.options] : filter.options
                          }
                          label=""
                          placeholder={`Select ${filter.label}`}
                          menuPosition="fixed"
                          findSelectedMatchUsing={filter.key === 'status' && isClientRole ? 'label' : 'value'}
                          maxToShow={1}
                        />
                      )}
                    </div>
                  )}

                  {/* Delete Button */}
                  <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <button
                      className="hover-text-danger"
                      onClick={() => handleRemoveFilter(filter.key)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'gray',
                        height: '40px',
                        width: '40px'
                      }}
                      title="Remove filter"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* footer */}
      <div className="mt-2 d-flex justify-content-between align-items-center p-2 border-top">
        <button
          className="btn btn-ghost d-flex align-items-center hover-light"
          style={{ color: '#f3c407' }}
          onClick={handleAddFilter}
          disabled={availableToAdd.length === 0}
        >
          <Plus size={16} className="mr-1" /> Add filter
        </button>
        <div className="d-flex">
          <Button
            variant="outline-secondary"
            className="mr-2"
            onClick={handleClearAll}
            style={{ backgroundColor: 'white', borderColor: 'lightgray', boxShadow: 'none', color: '#000' }}
          >
            Clear all
          </Button>
          <Button
            variant="success"
            style={{ backgroundColor: '#FACC15', borderColor: '#FACC15', boxShadow: 'none', color: '#000' }}
            onClick={handleApply}
          >
            Apply filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderFilterBuilder;
